import { useParams, useSearchParams } from "react-router-dom";
import useUserCourseMaterials from "../hooks/useUserCourseMaterials";
import useCalendarExport from "../hooks/useCalendarExport";
import useCourseDays from "../hooks/useCourseDays";
import { FaCalendarAlt } from "react-icons/fa";

function parseGermanDateString(str) {
  if (!str) return null;
  const [day, month, year] = str.split(".");
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
}

function safeJson(str, fallback = []) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

const CourseCockpitSection = () => {
  const { courseId } = useParams();
  const [search] = useSearchParams();
  const userId = search.get("user");
  const registrationId = search.get("registration");
  const courseTitle = search.get("title");
  const formattedDate = search.get("date");

  const { downloadICS, getGoogleCalendarLink } = useCalendarExport();
  const { materials, loading, error } = useUserCourseMaterials(userId, courseId);
  const { days = [], loading: daysLoading, error: daysError } = useCourseDays(registrationId || 0);

  if (loading)
    return (
      <p className="text-xs text-[#EFEBDC]/40 font-mono">
        <span className="text-[#AC8E66]/60">&gt;</span> lade kursmaterialien...
      </p>
    );
  if (error)
    return (
      <p className="text-xs text-red-400/80 font-mono">
        <span className="text-red-400/60">&gt;</span> error: {error}
      </p>
    );
  if (!materials)
    return (
      <p className="text-xs text-[#EFEBDC]/40 font-mono">
        <span className="text-[#AC8E66]/60">&gt;</span> keine inhalte verfuegbar.
      </p>
    );

  const videos = safeJson(materials.video_links, []);
  const dayPlan = safeJson(materials.day_plan_json, {});

  const shownTitle = courseTitle || materials.course_title || "";
  const shownDate = formattedDate || materials.custom_date || materials.start_date || "nicht festgelegt";

  const parsedDate =
    shownDate !== "nicht festgelegt" && shownDate.includes(".")
      ? parseGermanDateString(shownDate)
      : shownDate !== "nicht festgelegt"
      ? new Date(shownDate)
      : null;

  const calBtnClass =
    "px-3 py-1.5 text-[10px] font-mono text-[#AC8E66] bg-[#1c1c1c] border border-[#AC8E66]/30 rounded hover:border-[#AC8E66]/60 transition-all cursor-pointer";

  return (
    <div className="space-y-4">
      {/* Header Terminal */}
      <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-xs text-[#AC8E66] select-none">cockpit</span>
        </div>
        <div className="px-5 sm:px-6 py-5 bg-[#151515] font-mono">
          <p className="text-sm text-[#EFEBDC]/50 mb-2">
            <span className="text-[#AC8E66]">$</span> course --load
          </p>
          {shownTitle && (
            <p className="text-sm text-[#EFEBDC]/90 font-semibold mb-1 ml-3">{shownTitle}</p>
          )}
          <p className="text-xs text-[#AC8E66] ml-3">
            <span className="text-[#AC8E66]/60">&gt;</span> start: {parsedDate ? parsedDate.toLocaleDateString("de-DE") : shownDate}
          </p>
        </div>
      </div>

      {/* Kurstage */}
      <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-lg shadow-black/30">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-xs text-[#AC8E66] select-none">kurstage</span>
        </div>
        <div className="px-5 sm:px-6 py-5 bg-[#151515] font-mono">
          <p className="text-sm text-[#EFEBDC]/50 mb-3">
            <span className="text-[#AC8E66]">$</span> schedule --list
          </p>
          {daysLoading ? (
            <p className="text-xs text-[#EFEBDC]/40">
              <span className="text-[#AC8E66]/60">&gt;</span> lade kurstage...
            </p>
          ) : daysError ? (
            <p className="text-xs text-red-400/80">
              <span className="text-red-400/60">&gt;</span> error: {daysError}
            </p>
          ) : days.length > 0 ? (
            <div className="space-y-3">
              {days.map((d) => {
                const eventDate =
                  typeof d.date === "string" && d.date.includes(".")
                    ? parseGermanDateString(d.date)
                    : new Date(d.date);
                return (
                  <div key={d.id} className="border border-[#AC8E66]/10 rounded px-4 py-3 bg-[#1c1c1c]">
                    <div className="flex items-center gap-2 mb-2">
                      <FaCalendarAlt className="text-[#AC8E66]/60 text-xs" />
                      <span className="text-xs text-[#AC8E66]">
                        {eventDate.toLocaleDateString("de-DE")}
                      </span>
                      <span className="text-xs text-[#EFEBDC]/50">– {d.title}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className={calBtnClass}
                        onClick={() =>
                          downloadICS({
                            title: shownTitle,
                            date: eventDate,
                            location: "Microsoft Teams",
                            description: "Kurstag mit Denis Bitter",
                          })
                        }
                      >
                        [ ics → ]
                      </button>
                      <a
                        href={getGoogleCalendarLink({
                          title: shownTitle,
                          date: eventDate,
                          location: "Microsoft Teams",
                          description: "Kurstag mit Denis Bitter",
                        })}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <button className={calBtnClass}>[ google → ]</button>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-[#EFEBDC]/40 space-y-1 ml-3">
              <p><span className="text-[#AC8E66]/60">&gt;</span> kurstage werden zusammengestellt.</p>
              <p><span className="text-[#AC8E66]/60">&gt;</span> du bekommst eine email.</p>
            </div>
          )}
        </div>
      </div>

      {/* Materialien Terminal */}
      <div className="rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-lg shadow-black/30">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-xs text-[#AC8E66] select-none">materialien</span>
        </div>
        <div className="px-5 sm:px-6 py-5 bg-[#151515] font-mono space-y-6">

          {/* Teams Link */}
          <div>
            <p className="text-sm text-[#EFEBDC]/50 mb-1">
              <span className="text-[#AC8E66]">$</span> link --teams
            </p>
            {materials.teams_link ? (
              <a
                href={materials.teams_link}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[#AC8E66] hover:text-[#AC8E66]/80 underline underline-offset-2 ml-3 break-all transition-colors"
              >
                {materials.teams_link}
              </a>
            ) : (
              <p className="text-xs text-[#EFEBDC]/30 ml-3">
                <span className="text-[#AC8E66]/40">&gt;</span> kein link vorhanden.
              </p>
            )}
          </div>

          {/* Notizen */}
          <div>
            <p className="text-sm text-[#EFEBDC]/50 mb-1">
              <span className="text-[#AC8E66]">$</span> cat notizen.txt
            </p>
            {materials.personal_notes ? (
              <p className="text-xs text-[#EFEBDC]/60 leading-relaxed ml-3">{materials.personal_notes}</p>
            ) : (
              <p className="text-xs text-[#EFEBDC]/30 ml-3">
                <span className="text-[#AC8E66]/40">&gt;</span> keine notizen.
              </p>
            )}
          </div>

          {/* Videos */}
          <div>
            <p className="text-sm text-[#EFEBDC]/50 mb-1">
              <span className="text-[#AC8E66]">$</span> ls videos/
            </p>
            {videos.length > 0 ? (
              <div className="space-y-1 ml-3">
                {videos.map((v, i) => (
                  <a
                    key={v || i}
                    href={v}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-xs text-[#AC8E66] hover:text-[#AC8E66]/80 underline underline-offset-2 break-all transition-colors"
                  >
                    {v}
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#EFEBDC]/30 ml-3">
                <span className="text-[#AC8E66]/40">&gt;</span> keine videos vorhanden.
              </p>
            )}
          </div>

          {/* Tagesplan */}
          <div>
            <p className="text-sm text-[#EFEBDC]/50 mb-1">
              <span className="text-[#AC8E66]">$</span> cat tagesplan.json
            </p>
            {Object.keys(dayPlan).length > 0 ? (
              <div className="space-y-3 ml-3">
                {Object.entries(dayPlan).map(([day, topics]) => (
                  <div key={day}>
                    <p className="text-xs text-[#AC8E66] font-semibold">{day}</p>
                    {Array.isArray(topics) ? (
                      <div className="space-y-0.5 ml-2">
                        {topics.map((t, i) => (
                          <p key={`${t.time}-${t.topic}-${i}`} className="text-xs text-[#EFEBDC]/50">
                            <span className="text-[#EFEBDC]/20 mr-1">•</span>
                            <span className="text-[#AC8E66]/60">{t.time}</span>
                            <span className="text-[#EFEBDC]/20 mx-1">–</span>
                            {t.topic}: {t.details}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#EFEBDC]/50 ml-2">{topics.toString()}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-[#EFEBDC]/30 ml-3">
                <span className="text-[#AC8E66]/40">&gt;</span> kein tagesplan verfuegbar.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCockpitSection;
